import {ChartData, ChartDataset, ChartOptions, Point,} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {useEffect, useState} from "react";
import {Chart, ChartProps} from '../Chart/Chart';
import {LayoutPosition} from "chart.js/dist/types/layout";

export type LineChartBucketData =  Omit<ChartDataset<'line'>, 'label' | 'data' | 'fill'> & {
  stacked?: LineChartStackProps
  values: (number | Point | null)[]
}

export type LineChartData = {
  titles: string[]
  buckets: Record<string, LineChartBucketData>
}

export type LineChartStackProps = {
  enabled?: boolean
  filled?: boolean
}

export type LineChartProps = Omit<ChartProps<'line'>, 'type' | 'options' | 'data'> & {
  stacked?: LineChartStackProps
  xAxisTitle?: string
  yAxisTitle?: string
  tooltipPos?: LayoutPosition
  data?: LineChartData
};

export const LineChart = (props: LineChartProps) => {
  const {
    stacked,
    xAxisTitle,
    yAxisTitle,
    tooltipPos,
    data,
    ...rest
  } = props;

  const [chartOptions, setChartOptions] = useState<
    ChartOptions<'line'> | undefined
  >(undefined);

  const [chartData, setChartData] = useState<
    ChartData<'line'> | undefined
  >(undefined);

  useEffect(() => {
    setChartOptions(getChartOptions(stacked, xAxisTitle, yAxisTitle, tooltipPos))
  }, [stacked, tooltipPos, xAxisTitle, yAxisTitle])

  useEffect(() => {
    setChartData(getChartData(data, stacked))
  }, [data, stacked])

  return <Chart {...rest} type="line" options={chartOptions} data={chartData}/>
};


const getChartOptions = (stacked: LineChartStackProps | undefined, xAxisTitle: string | undefined, yAxisTitle: string | undefined, tooltipPos : LayoutPosition | undefined): ChartOptions<'line'> => {
  return {
    maintainAspectRatio: true,
    animation: false,
    responsive: true,
    interaction: {
      intersect: false,
    },
    spanGaps: true,
    scales: {
      x: {
        stacked: stacked?.enabled,
        title: {
          display: true,
          text: xAxisTitle,
        },
      },
      y: {
        stacked: stacked?.enabled,
        beginAtZero: true,
        title: {
          display: true,
          text: yAxisTitle
        },
      }
    },
    plugins: {
      legend: {
        position: tooltipPos ?? 'top',
      },
      datalabels: {
        display: false
      }
    }
  };
};

const getChartData = (data: LineChartData | undefined, stacked: LineChartStackProps | undefined): ChartData<'line'> => {
  const chartDataSets: ChartDataset<'line'>[] = [];

  if (data) {
    for (const key of Object.keys(data.buckets)) {
      const bucket = data.buckets[key]
      const stackEnabled = bucket?.stacked?.enabled ?? stacked?.enabled
      const stackFilled = bucket?.stacked?.filled ?? stacked?.filled

      chartDataSets.push({
        ...bucket,
        label: key,
        data: bucket.values,
        fill: (stackEnabled) ? stackFilled : undefined,
      });
    }
  }

  return {
    labels: data?.titles,
    datasets: chartDataSets,
  };
};
